import { Directive, Input, ElementRef, Injector } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { isNil } from 'lodash-es';
import numeral from 'numeral';
import { UtilsService } from '../utils.service';

// NOTE: ideally this shouldn't be a ControlValueAccessor, but rather a parser/formatter
// but that is still not supported in Angular - see https://github.com/angular/angular/issues/3009
@Directive({
  standalone: false,
  selector: 'input[numberBox]',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: NumberBoxDirective, multi: true },
  ],
  host: {
    '[format]': 'format',
    '[disabled]': 'disabled',
    '(input)': '_onInput($event.target.value)',
    '(blur)': '_onBlur()',
    '(keydown)': '_onKeydown($event)',
  }
})
export class NumberBoxDirective implements ControlValueAccessor {

  _format: string;
  @Input() get format(): string { return this._format; }
  set format(value: string) { this._format = value; this.resetFormat(); }

  _innerValue: number;
  formattedValue = null;
  disabled: boolean;

  constructor(
    private utils: UtilsService,
    protected _elementRef: ElementRef<HTMLInputElement>
  ) {
  }

  _onInput(value) {
    this.onValueChanged(value);
  }

  _onBlur() {
    this.onTouch();
    this.formatValue(this.value);
  }

  _onKeydown(event: KeyboardEvent) {
    if (!this.disabled && event.key === 'Enter') {
      this.onTouch();
      this.formatValue(this.value);
      event.preventDefault();
    }
  }

  resetFormat() {
    this.formatValue(this.value);
  }

  onValueChanged(value: string) {
    const parsedValue = this.parseValue(value);

    if (this.value !== parsedValue) {
      this.value = parsedValue;
    }
  }

  formatValue(value: number) {
    let formattedValue;
    if (!isNil(value)) {
      formattedValue = this.format ?
        this.utils.numberFormat(value, this.format) :
        value.toString();
    } else {
      formattedValue = value;
    }
    this.formattedValue = formattedValue;
    this._elementRef.nativeElement.value = this.formattedValue;
  }

  parseValue(value: string): number {
    let parsedValue = null;
    if (this.utils.isDefinedTrimmed(value)) {
      parsedValue = this.format ?
        // the format is also a parse format
        numeral(numeral(value).format(this.format)).value() :
        numeral(value).value();
    }

    return parsedValue;
  }

  onChange: any = () => { };
  onTouch: any = () => { };

  get value() {
    return this._innerValue;
  }

  set value(val: number) {
    this._innerValue = val;
    this.onChange(val);
  }

  writeValue(value: number): void {
    this._innerValue = value;
    this.formatValue(value);
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}